import clsx from 'clsx';
import { type PropsWithChildren } from 'react';
import { useLocale } from 'remix-i18next/react';
import { useTranslatedPath } from '#common/utils/i18n.tsx';
import { useVoicePageRootData } from '#speaker-shop/hooks/useVoicePageRootData';
import { useVoicePath } from '#speaker-shop/hooks/useVoicePath';
import LanguagePicker from '../../common/components/LanguagePicker';
import { GridPattern } from './GridPattern';
import { Portrait } from './Portrait';
import { StarRating } from './StarRating';

interface Opts {
    faceSize?: 'small' | 'large';
    showVideoIfAvailable?: boolean;
    rating?: number;
    ratingClass?: string;
}

export function FaceLayout(
    { children, faceSize = 'large', showVideoIfAvailable = false, rating, ratingClass }: PropsWithChildren<Opts>,
) {
    const { baseUrl, portraitImageSrc, voiceNameSvg, speakerNameSvg, portraitVideoHlsSrc, portraitVideoHlsAssetPath } =
        useVoicePageRootData();

    const translatePath = useTranslatedPath(useLocale());
    const voicePath = useVoicePath();
    return (
        <header className='overflow-hidden bg-slate-100 lg:bg-transparent lg:px-5'>
            <div className='absolute top-5 left-5 md:top-10 md:left-10 text-white font-bold text-lg md:text-3xl z-20'>
                <LanguagePicker baseUrl={baseUrl} />
            </div>
            <div className='mx-auto grid lg:max-w-10xl grid-cols-1 gap-y-16 pt-16 md:pt-20 lg:grid-cols-12 lg:gap-y-20 lg:px-3 lg:pt-20 lg:pb-36 xl:py-32'>
                <div
                    className={clsx(
                        'relative flex h-fit',
                        faceSize === 'large' ? 'lg:col-span-5' : 'lg:col-span-4',
                        faceSize === 'large' ? '3xl:col-span-4' : '3xl:col-span-3'
                    )}
                >
                    <div className='absolute -top-20 right-1/2 -bottom-12 left-0 z-10 rounded-br-6xl bg-crispybluedark-700 text-white/10 md:bottom-8 lg:-inset-y-32 lg:right-full lg:left-[-100vw] lg:-mr-[20vw] 3xl:-mr-[10vw]'>
                        <GridPattern x='100%' y='100%' patternTransform='translate(112 64)' />
                    </div>
                    <div className='mx-auto lg:ml-auto lg:mr-0 z-10'>
                        {!!rating && (
                            <a
                                href={translatePath(voicePath('/testimonials'))}
                                className={clsx('flex flex-row items-center mb-2 mx-auto', ratingClass)}
                            >
                                <StarRating rating={rating} size='small' />
                            </a>
                        )}

                        <Portrait
                            voiceNameSvg={voiceNameSvg}
                            speakerNameSvg={speakerNameSvg}
                            videoHlsSrc={portraitVideoHlsSrc}
                            videoHlsAssetPath={portraitVideoHlsAssetPath}
                            showVideoIfAvailable={showVideoIfAvailable}
                            imageSrc={portraitImageSrc}
                            size={faceSize}
                        />
                    </div>
                </div>
                <div
                    className={clsx(
                        faceSize === 'large' ? 'lg:col-span-7 3xl:col-span-8' : 'lg:col-span-8 3xl:col-span-9',
                        'lg:pl-16 xl:pl-20 flex flex-col'
                    )}
                >
                    {children}
                </div>
            </div>
        </header>
    );
}
